import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import TopBar from "@/sot/primitives/TopBar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  acceptPersistedPharmacyStaffInvite,
  acceptPharmacyStaffInvite,
  mergePersistedPharmacyMembers,
  readPharmacyProfileForActor,
  savePharmacyProfile,
} from "@/services/pharmacyLaneService.js";

function readInviteParams(search = "") {
  try {
    const params = new URLSearchParams(search || "");
    const email = String(params.get("email") || "").trim().toLowerCase();
    const member = String(params.get("member") || "").trim();
    const next =
      String(params.get("next") || "").trim().startsWith("/")
        ? String(params.get("next") || "").trim()
        : "/pharmacy/dashboard";
    return { email, member, next };
  } catch {
    return { email: "", member: "", next: "/pharmacy/dashboard" };
  }
}

export default function PharmacyInviteLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth() || {};
  const { t } = useLanguage();
  const [busy, setBusy] = React.useState(false);
  const { email, member, next } = React.useMemo(
    () => readInviteParams(location.search),
    [location.search],
  );

  const activeEmail = String(auth?.user?.email || "").trim().toLowerCase();
  const alreadySignedIn = !!activeEmail && !!email && activeEmail === email;
  const requiresSessionSwitch = !!activeEmail && !!email && activeEmail !== email;
  const missingInviteEmail = !email;

  async function continueToInviteLogin() {
    if (busy) return;
    setBusy(true);
    if (alreadySignedIn) {
      try {
        const acceptedRows = await acceptPersistedPharmacyStaffInvite({
          membershipId: member,
          email,
          userId: String(auth?.user?.id || "").trim(),
        });

        const localProfile = readPharmacyProfileForActor({
          userId: String(auth?.user?.id || "").trim(),
          email,
        }) || {};
        let nextProfile = acceptPharmacyStaffInvite(localProfile, {
          email,
          userId: String(auth?.user?.id || "").trim(),
        });
        if (Array.isArray(acceptedRows) && acceptedRows.length) {
          nextProfile = mergePersistedPharmacyMembers(nextProfile, acceptedRows);
          nextProfile = acceptedRows.reduce(
            (current, row) => acceptPharmacyStaffInvite(current, {
              email: String(row?.email || row?.invited_email || email || "").trim(),
              userId: String(row?.userId || row?.member_user_id || auth?.user?.id || "").trim(),
            }),
            nextProfile,
          );
        }
        savePharmacyProfile(nextProfile);
      } catch (error) {
        console.warn(
          "[PharmacyInviteLanding] invite activation reconciliation skipped:",
          error?.message || error,
        );
      }
      navigate(next, { replace: true });
      return;
    }

    const params = new URLSearchParams();
    params.set("sheet", "1");
    params.set("tab", "email");
    params.set("invite", "pharmacy");
    if (email) params.set("email", email);
    if (member) params.set("member", member);
    if (next) params.set("next", next);

    navigate(`/login?${params.toString()}`, { replace: true });
  }

  async function handleContinue() {
    if (busy) return;
    await continueToInviteLogin();
  }

  return (
    <div className="min-h-dvh bg-[rgb(var(--clr-bg))] text-[rgb(var(--clr-on-surface))]">
      <PharmacyDesktopHeader section="settings" notifications={0} />

      <main className="afrimed-rail afrimed-rail--comfortable pt-3 pb-12">
        <TopBar
          title={t("pharmacy.invite.title", "Pharmacy invitation")}
          subtitle={t(
            "pharmacy.invite.subtitle",
            "Review your AfriMed Pharmacy team invite, confirm the invited email, then request the secure sign-in link for that exact pharmacy member account.",
          )}
          onBack={() => navigate("/login")}
          titleWrapperClassName="mt-2 mb-5"
        />

        <div className="w-full max-w-[1120px] space-y-6">
          <p className="text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
            {t(
              "pharmacy.invite.body",
              "AfriMed will use the invited email below for the pharmacy member sign-in step. Open the newest sign-in email on this same browser or device, then AfriMed will continue to the Pharmacy Dashboard.",
            )}
          </p>

          <p className="text-body-medium text-[rgb(var(--clr-on-surface-variant))]">
            {t(
              "pharmacy.invite.demoSeedCopy",
              "After launching your Pharmacy Dashboard, you will find seeded demo stock and up to three sample prescription fulfilment requests. Please use these to explore how AfriMed™ supports pharmacy preparation, verification, dispensing, payments, and fulfilment workflows during staging.",
            )}
          </p>

          {missingInviteEmail ? (
            <div className="rounded-[12px] bg-[rgba(var(--clr-error),0.10)] px-4 py-3 text-body-medium text-[rgb(var(--clr-error))]">
              {t(
                "pharmacy.invite.emailMissingBody",
                "This invite link is missing the invited email. Return to the sender and request the latest AfriMed pharmacy invite.",
              )}
            </div>
          ) : null}

          <div>
            <TextField56
              tone="surface"
              label={t("pharmacy.invite.emailLabel", "Invited email")}
              value={email || t("pharmacy.invite.emailMissing", "No invited email was attached to this link.")}
              onChange={() => {}}
              readOnly
            />
          </div>

          {alreadySignedIn ? (
            <div className="rounded-[12px] bg-[rgba(0,189,196,0.10)] px-4 py-3 text-body-medium text-[#008A8F]">
              {t(
                "pharmacy.invite.alreadySignedIn",
                "This invited email is already signed in on this browser. Continue straight to Pharmacy Dashboard.",
              )}
            </div>
          ) : null}

          {requiresSessionSwitch ? (
            <div className="rounded-[12px] bg-[rgba(24,50,119,0.08)] px-4 py-3 text-body-medium text-[rgb(var(--clr-on-surface))]">
              {t(
                "pharmacy.invite.switchEmail",
                "A different account is signed in on this browser. Continue with the invited email so AfriMed can connect the correct pharmacy member account.",
              )}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" onClick={() => void handleContinue()} disabled={busy}>
              {alreadySignedIn
                ? t("pharmacy.invite.cta.dashboard", "Continue to Pharmacy Dashboard")
                : busy
                  ? t("pharmacy.invite.cta.opening", "Opening sign in...")
                  : requiresSessionSwitch
                    ? t("pharmacy.invite.cta.invitedEmail", "Continue with invited email")
                    : missingInviteEmail
                      ? t("pharmacy.invite.cta.returnLogin", "Return to sign in")
                      : t("pharmacy.invite.cta.requestLink", "Continue to request sign-in link")}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
